// @givmo/board — the getting-started list's dialogs (CBG-YR4-ACK, -START): a signed paper form, a
// board buddy, the one-way close, and changing a list.
//
// Each dialog collects what its act needs and nothing else; the page that opens it owns the request.
// The paper form records the member's confirmation as the officer's entry of a paper the member
// signed — never a signature by the officer — so it names the documents, the date on the paper and
// where the paper is, and nothing more. The close is one way (C16): its dialog says so before it acts,
// and it is offered from a person's list editor, never beside the card's own acts.

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  RowActions,
  Select,
  TextField,
} from '@givmo/design-system';
import type { BoardMemberGettingStartedItemOut } from '@givmo/api-client';
import { useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { FieldNote } from '../../components/field-note';
import { todayAsDateInput } from '../../lib/display';
import {
  documentKindLabel,
  extractGoverningDocuments,
  requiresLabel,
  versionInForce,
} from '../govdocs/govdocs-decode';
import {
  asDocumentKind,
  chainKey,
  itemDetail,
  itemTitle,
  type ItemSpec,
} from './onboarding-decode';
import { onboardingWords as w } from './onboarding-words';

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const SMALL = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';

function Actions({
  pending,
  canConfirm,
  confirmLabel,
  tone = 'primary',
  onCancel,
  onConfirm,
  testIdPrefix,
  aside,
}: {
  pending: boolean;
  canConfirm: boolean;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  onCancel: () => void;
  onConfirm: () => void;
  testIdPrefix: string;
  /** A quieter act at the row's start (the person's list's one-way close). */
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-10">
      {aside !== undefined ? <div className="mr-auto">{aside}</div> : null}
      <Button
        appearance="saas"
        tone="secondary"
        disabled={pending}
        onClick={onCancel}
        testId={`${testIdPrefix}-cancel`}
      >
        {w.cancel}
      </Button>
      <Button
        appearance="saas"
        tone={tone}
        loading={pending}
        disabled={!canConfirm || pending}
        onClick={onConfirm}
        testId={`${testIdPrefix}-confirm`}
      >
        {confirmLabel}
      </Button>
    </div>
  );
}

// ── A signed paper form ─────────────────────────────────────────────────────────────────────────

export interface PaperFormValues {
  records: { record_id: string; content_hash: string }[];
  signed_on: string;
  note: string;
}

export function PaperFormDialog({
  memberName,
  candidates,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  memberName: string;
  candidates: readonly BoardMemberGettingStartedItemOut[];
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (values: PaperFormValues) => void;
}) {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set());
  const [signedOn, setSignedOn] = useState(todayAsDateInput());
  const [note, setNote] = useState('');
  const records = candidates
    .filter((item) => chosen.has(item.id))
    .map((item) => ({
      record_id: item.target_record_id ?? '',
      content_hash: item.target_content_hash ?? '',
    }));
  const canConfirm = records.length > 0 && signedOn.length > 0 && note.trim().length > 0;
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content
        aria-label={w.paperTitle}
        className="flex flex-col gap-16"
        testId="gs-paper-dialog"
      >
        <Dialog.Title className={TITLE}>{w.paperTitle}</Dialog.Title>
        {candidates.length === 0 ? (
          <p className={LINE}>{w.paperNothingToRecord}</p>
        ) : (
          <>
            <p className={LINE}>{w.paperLead(memberName)}</p>
            <ul className="flex flex-col gap-10">
              {candidates.map((item) => {
                const detail = itemDetail(item);
                return (
                  <li key={item.id} className="flex items-start gap-10">
                    <Checkbox
                      appearance="saas"
                      checked={chosen.has(item.id)}
                      onCheckedChange={(checked) =>
                        setChosen((current) => {
                          const next = new Set(current);
                          if (checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        })
                      }
                      aria-label={itemTitle(item)}
                      testId={`gs-paper-pick-${item.id}`}
                    />
                    <span className="flex min-w-0 flex-col gap-2">
                      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {itemTitle(item)}
                      </span>
                      {detail !== null ? <span className={SMALL}>{detail}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
            <TextField
              label={w.paperDate}
              type="date"
              value={signedOn}
              onChange={(event) => setSignedOn(event.target.value)}
              testId="gs-paper-date"
            />
            <TextField
              label={w.paperNote}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              testId="gs-paper-note"
            />
            <FieldNote>{w.paperNoteHint}</FieldNote>
          </>
        )}
        {error !== null ? <ErrorMessage testId="gs-paper-error">{error}</ErrorMessage> : null}
        <Actions
          pending={pending}
          canConfirm={canConfirm}
          confirmLabel={w.paperRecord}
          onCancel={onCancel}
          onConfirm={() => onConfirm({ records, signed_on: signedOn, note: note.trim() })}
          testIdPrefix="gs-paper"
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── A board buddy ───────────────────────────────────────────────────────────────────────────────

export function BuddyDialog({
  firstName,
  people,
  currentBuddyId,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  firstName: string;
  people: readonly { id: string; label: string }[];
  currentBuddyId: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (buddyId: string) => void;
}) {
  const [buddy, setBuddy] = useState<string | null>(currentBuddyId);
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content
        aria-label={w.buddyDialogTitle(firstName)}
        className="flex flex-col gap-16"
        testId="gs-buddy-dialog"
      >
        <Dialog.Title className={TITLE}>{w.buddyDialogTitle(firstName)}</Dialog.Title>
        <p className={LINE}>{w.buddyDialogLead(firstName)}</p>
        <Select
          appearance="saas"
          label={w.buddyField}
          placeholder={w.buddyPlaceholder}
          value={buddy}
          onValueChange={setBuddy}
          options={people.map((person) => ({ value: person.id, label: person.label }))}
          testId="gs-buddy-select"
        />
        {error !== null ? <ErrorMessage testId="gs-buddy-error">{error}</ErrorMessage> : null}
        <Actions
          pending={pending}
          canConfirm={buddy !== null && buddy !== currentBuddyId}
          confirmLabel={w.buddyChoose}
          onCancel={onCancel}
          onConfirm={() => buddy !== null && onConfirm(buddy)}
          testIdPrefix="gs-buddy"
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── The one-way close (C16) ─────────────────────────────────────────────────────────────────────

export function CloseListDialog({
  firstName,
  pending,
  onCancel,
  onConfirm,
}: {
  firstName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content
        aria-label={w.closeTitle(firstName)}
        className="flex flex-col gap-16"
        testId="gs-close-dialog"
      >
        <Dialog.Title className={TITLE}>{w.closeTitle(firstName)}</Dialog.Title>
        <p className={LINE}>{w.closeBody}</p>
        <Actions
          pending={pending}
          canConfirm
          tone="danger"
          confirmLabel={w.closeConfirm}
          onCancel={onCancel}
          onConfirm={onConfirm}
          testIdPrefix="gs-close"
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── Changing a list ─────────────────────────────────────────────────────────────────────────────

/** A governing document the board recorded, as the editor offers it. */
export interface DocumentChoice {
  kind: string;
  label: string | null;
}

function specTitle(spec: ItemSpec, titles: ReadonlyMap<string, string>): string {
  if (spec.item_kind === 'read') {
    if (spec.governing_document_kind) {
      const known = titles.get(
        chainKey(spec.governing_document_kind, spec.governing_document_label),
      );
      if (known !== undefined) return known;
      const label = (spec.governing_document_label ?? '').trim();
      const kind = documentKindLabel(spec.governing_document_kind);
      return label.length > 0 ? `${kind} — ${label}` : kind;
    }
    return titles.get(spec.record_id ?? '') ?? '';
  }
  return itemTitle({ item_kind: spec.item_kind, step_text: spec.step_text, position: 0 });
}

export function ListEditorDialog({
  title,
  lead,
  initial,
  titles,
  documents,
  pending,
  error,
  onCancel,
  onSave,
  onCloseList,
  documentsFailed = false,
}: {
  title: string;
  lead: string;
  initial: readonly ItemSpec[];
  /** Each item's title as the list shows it (`titlesOf`), so the editor names it the same way. */
  titles: ReadonlyMap<string, string>;
  documents: readonly DocumentChoice[];
  /** The documents' read failed: the add-a-document row says so in its place. */
  documentsFailed?: boolean;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (items: ItemSpec[]) => void;
  /** A person's list only: opens the one-way close's own dialog (C16). */
  onCloseList?: () => void;
}) {
  const [items, setItems] = useState<ItemSpec[]>(() => initial.map((item) => ({ ...item })));
  const [documentChoice, setDocumentChoice] = useState<string | null>(null);
  const [stepText, setStepText] = useState('');
  const onList = useMemo(
    () =>
      new Set(
        items
          .filter((item) => item.item_kind === 'read' && item.governing_document_kind)
          .map((item) => chainKey(item.governing_document_kind, item.governing_document_label)),
      ),
    [items],
  );
  const offered = documents.filter((doc) => !onList.has(chainKey(doc.kind, doc.label)));
  const move = (index: number, by: -1 | 1) =>
    setItems((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  const addDocument = () => {
    const doc = offered.find((choice) => chainKey(choice.kind, choice.label) === documentChoice);
    if (doc === undefined) return;
    setItems((current) => [
      ...current,
      {
        item_kind: 'read',
        governing_document_kind: asDocumentKind(doc.kind),
        governing_document_label: requiresLabel(doc.kind) ? doc.label : null,
      },
    ]);
    setDocumentChoice(null);
  };
  const addStep = () => {
    const words = stepText.trim();
    if (words.length === 0) return;
    setItems((current) => [...current, { item_kind: 'step', step_text: words }]);
    setStepText('');
  };
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content aria-label={title} className="flex flex-col gap-16" testId="gs-editor-dialog">
        <Dialog.Title className={TITLE}>{title}</Dialog.Title>
        <p className={LINE}>{lead}</p>
        <ol className="flex flex-col">
          {items.map((item, index) => (
            <li
              key={`${item.id ?? 'new'}-${index}`}
              className="flex items-center justify-between gap-12 border-t border-border-subtle py-6 first:border-t-0"
              data-testid={`gs-editor-row-${index}`}
            >
              <span className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {specTitle(item, titles)}
              </span>
              <RowActions
                testId={`gs-editor-actions-${index}`}
                label={w.rowActions(specTitle(item, titles))}
                actions={[
                  {
                    label: w.moveUp,
                    testId: `gs-editor-up-${index}`,
                    disabled: index === 0,
                    onSelect: () => move(index, -1),
                  },
                  {
                    label: w.moveDown,
                    testId: `gs-editor-down-${index}`,
                    disabled: index === items.length - 1,
                    onSelect: () => move(index, 1),
                  },
                  {
                    label: w.remove,
                    testId: `gs-editor-remove-${index}`,
                    destructive: true,
                    onSelect: () => setItems((current) => current.filter((_, at) => at !== index)),
                  },
                ]}
              />
            </li>
          ))}
        </ol>
        {documentsFailed ? (
          <ErrorMessage testId="gs-editor-documents-error">{w.documentsFailed}</ErrorMessage>
        ) : null}
        {offered.length > 0 ? (
          <div className="flex flex-wrap items-end gap-10">
            <div className="min-w-[220px] flex-1">
              <Select
                appearance="saas"
                label={w.addDocument}
                placeholder={w.addDocumentPlaceholder}
                value={documentChoice}
                onValueChange={setDocumentChoice}
                options={offered.map((doc) => ({
                  value: chainKey(doc.kind, doc.label),
                  label: doc.label
                    ? `${documentKindLabel(doc.kind)} — ${doc.label}`
                    : documentKindLabel(doc.kind),
                }))}
                testId="gs-editor-document"
              />
            </div>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={documentChoice === null}
              onClick={addDocument}
              testId="gs-editor-add-document"
            >
              {w.add}
            </Button>
          </div>
        ) : null}
        <div className="flex flex-wrap items-end gap-10">
          <div className="min-w-[220px] flex-1">
            <TextField
              label={w.addStep}
              placeholder={w.stepPlaceholder}
              value={stepText}
              maxLength={200}
              onChange={(event) => setStepText(event.target.value)}
              testId="gs-editor-step"
            />
          </div>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={stepText.trim().length === 0}
            onClick={addStep}
            testId="gs-editor-add-step"
          >
            {w.add}
          </Button>
        </div>
        {error !== null ? <ErrorMessage testId="gs-editor-error">{error}</ErrorMessage> : null}
        <Actions
          pending={pending}
          canConfirm
          confirmLabel={w.save}
          onCancel={onCancel}
          onConfirm={() => onSave(items)}
          testIdPrefix="gs-editor"
          aside={
            onCloseList === undefined ? undefined : (
              <Button
                appearance="saas"
                tone="ghost"
                disabled={pending}
                onClick={onCloseList}
                testId="gs-editor-close-list"
              >
                {w.closeList}
              </Button>
            )
          }
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * The governing documents a list may name — each with a version in force — read when an editor
 * opens. `documents` is `undefined` until served; a failed read serves none and says so in the
 * editor (`failed`), which still opens: its order, removals and steps don't need the read.
 */
export function useDocumentChoices(open: boolean): {
  documents: DocumentChoice[] | undefined;
  failed: boolean;
} {
  const operations = useBoardOperations();
  const { orgRef, isReady } = useBoardHost();
  const query = useQuery({
    queryKey: ['board-getting-started-documents', orgRef],
    enabled: isReady && orgRef !== null && open,
    queryFn: async (): Promise<DocumentChoice[]> => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractGoverningDocuments(data)
        .filter((document) => versionInForce(document) !== null)
        .map((document) => ({ kind: document.document_kind, label: document.label ?? null }));
    },
  });
  return { documents: query.data ?? (query.isError ? [] : undefined), failed: query.isError };
}
